import type { ArtifactComposedEvent } from "./agui";
import type { Artifact, PanelResult } from "../types";

/**
 * A stored document, shaped as the event the document reader takes.
 *
 * The reader was built for documents the agent streams, which carry preview
 * rows per chart section. A stored document has cached results instead, so
 * they are reshaped into previews here and the document opens painted rather
 * than empty with a refresh button on every section.
 */
export function composedEventFor(
  artifact: Artifact,
  results: PanelResult[],
): ArtifactComposedEvent {
  const byPanel = new Map(results.map((result) => [result.panelId, result]));
  const previews = artifact.sections.flatMap((section, index) => {
    const panelId = "panelId" in section ? section.panelId : undefined;
    const result = panelId ? byPanel.get(panelId) : undefined;
    if (!result || result.rows.length === 0) return [];
    return [
      {
        // Matched by index because that is how the reader looks previews up,
        // and the API preserves section order when it saves.
        sectionIndex: index,
        rows: result.rows,
        rowCount: result.rowCount,
        complete: !result.truncated,
      },
    ];
  });
  return {
    artifactId: artifact.id,
    title: artifact.title,
    saved: true,
    version: artifact.currentVersion,
    // Carried so a document that was already published reopens as such,
    // rather than offering to publish it a second time.
    status: artifact.status,
    spec: {
      artifactId: artifact.id,
      title: artifact.title,
      contextId: artifact.contextId,
      contextVersion: artifact.contextVersion,
      version: artifact.currentVersion,
      sections: artifact.sections,
      previews,
    },
  };
}
